/* Copyright (c) 2020-present CloudIO Inc */

import { getCustomChildItemTypes } from '../../registry/Manager';
import { page as SamplePage } from '../../help/sample_pages/SplitPanelPage';
import { preventIfParentPathContainsAutoLayout } from '../../utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  Item,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { verticalSplitFixedSide } from '../../registry/constants';
import { defaultValue } from './VerticalSplitPanel';
import { splitPanelChildTypes } from '../HorizontalSplitPanel/HorizontalSplitPanelManager';

const MIN_HEIGHT = 40;
const MAX_HEIGHT = 1800;
const name = 'Vertical Split Panel';

const VerticalSplitPanelManager: ComponentManagerFn<'VerticalSplitPanel'> = (
  platform: Platform,
): ComponentManager<'VerticalSplitPanel'> => {
  return {
    ...baseComponentManager<'VerticalSplitPanel'>(
      platform,
      'VerticalSplitPanel',
    ),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (
        [
          ...getCustomChildItemTypes('VerticalSplitPanel'),
          ...splitPanelChildTypes,
        ] as ItemType[]
      ).includes(childType) &&
      (item.children.length < 2 ? true : `${name} can only have two children!`),
    isAllowedInsideParent: (page, item) =>
      preventIfParentPathContainsAutoLayout(page, item, name),
    props: {
      fixedSide: {
        label: 'Fixed Side',
        type: 'select',
        options: verticalSplitFixedSide,
        required: true,
        category: 'Layout',
      },
      fixedWidthType: {
        label: 'Width Type',
        type: 'select',
        options: { px: 'Pixel', '%': 'Percent' },
        category: 'Layout',
      },
      fixedWidthPercent: {
        label: 'Width %',
        type: 'number',
        max: 95,
        min: 5,
        step: 5,
        category: 'Layout',
      },
      fixedWidth: {
        label: 'Width',
        type: 'number',
        max: MAX_HEIGHT,
        min: MIN_HEIGHT,
        step: 40,
        category: 'Layout',
      },
      fixedWidthMin: {
        label: 'Min Width',
        type: 'number',
        max: MAX_HEIGHT,
        min: MIN_HEIGHT,
        step: 40,
        category: 'Layout',
      },
      fixedWidthMax: {
        label: 'Max Width',
        type: 'number',
        max: MAX_HEIGHT,
        min: MIN_HEIGHT,
        step: 40,
        category: 'Layout',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Spacing',
      },
      border: {
        label: 'Border',
        type: 'border',
        category: 'Spacing',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'fixedWidthPercent':
          return itemData.fixedWidthType === '%';
        case 'fixedWidth':
          return itemData.fixedWidthType === 'px';
        default:
          return true;
      }
    },
    onReplace: (
      item: Item<'VerticalSplitPanel'>,
      fromItem: Item<'HorizontalSplitPanel'> | Item<'FlexPanel'>,
      page,
    ) => {
      if (fromItem.type === 'FlexPanel') {
        const children: string[] = [];
        fromItem.children.forEach((childId, index) => {
          const child = page[childId] as Item<'FlexItem'>;
          if (child.type === 'FlexItem') {
            if (child.props.sizeType === 'minmax' && child.props.max) {
              item.props.fixedWidth = child.props.max;
              item.props.fixedSide = index === 0 ? 'start' : 'end';
            }
            child.children.forEach((c) => {
              children.push(c);
              page[c] = { ...page[c], parent: item.itemId };
            });
          }
          delete page[childId];
        });
        item.children = children;
      } else if (fromItem.type === 'HorizontalSplitPanel') {
        item.props.fixedWidthType = fromItem.props.fixedHeightType;
        item.props.fixedWidth = fromItem.props.fixedHeight;
        item.props.fixedWidthPercent = fromItem.props.fixedHeightPercent;
        item.props.fixedWidthMin = fromItem.props.fixedHeightMin;
        item.props.fixedWidthMax = fromItem.props.fixedHeightMax;
      }
      return item;
    },
    category: 'Containers',
    name,
    canHaveChildren: () => true,
    layout: 'fixed',
    icon: { iconName: 'border-center-v' },
    replaceableWith: ['HorizontalSplitPanel', 'FlexPanel'],
    description: `**Overview**<br/>
The Vertical Split Panel is a layout container that divides a single area into two horizontally adjacent sections (Left and Right). Similar to its horizontal counterpart, this component features a draggable divider that allows users to interactively resize the width of each section.
This is the standard choice for "Sidebar + Content" layouts, such as a file explorer (folders on the left, files on the right) or a master-detail list (items on the left, details on the right).

**1. Defining the Anchor (Fixed Side)**<br/>
To maintain a responsive layout when the browser window is resized, one side of the panel must be designated as the "Fixed" anchor.
* **Fixed Side:** Determines which section retains its initial width while the other stretches to fill the remaining space.
* **Start (Left):** The left sidebar keeps its width; the right content area expands.
* **End (Right):** The right sidebar keeps its width; the left content area expands.

**2. Controlling Width & Limits**<br/>
You can define precise constraints for the fixed section to ensure the interface remains usable across different screen sizes.
* **Width Type:** Choose how the initial width is calculated. (Pixel sets a rigid static width, ideal for menus; Percent sets a relative width based on available space).
* **Width:** The default size of the fixed section when the page loads.
* **Min Width / Max Width:** Defines the resize boundaries. The user cannot drag the divider past these points, preventing a sidebar from becoming too narrow to read or too wide to leave space for the main content.
* **Note:** The system automatically enforces a safety minimum of 40px for both sections to prevent content from disappearing entirely.

**3. Content & Structure**<br/>
The Vertical Split Panel is strictly designed for two-column layouts.
* **Child Limit:** This component accepts exactly two children. (The first child becomes the Left section; the second child becomes the Right section).
* **Interchangeability:** If your layout needs change, you can convert this component directly into a Horizontal Split Panel or a Flex Panel using the replacement tools in the designer, preserving your child components.
* **Layout Safety:** Like other split panels, this component manages its own sizing logic and should not be placed inside parents that enforce conflicting auto-layout rules.`,
    helpRenders: [
      {
        title: 'Vertical Split Panel',
        description: `Adjust the various width props and see how it behaves. 

\`\`\`alert
Note: A minimum of 40px width will be maintained on either side irrespective of the min & max width configuration.
\`\`\`
`,
        page: SamplePage,
        exampleId: 'e1',
        height: 400,
        visibleProps: [
          'fixedWidthType',
          'fixedWidthPercent',
          'fixedWidth',
          'fixedWidthMax',
          'fixedWidthMin',
          'fixedSide',
        ],
        item: {
          itemId: 'horizontalSplitPanel',
          props: {
            fixedWidth: 300,
            fixedWidthMax: 1800,
            fixedWidthMin: 40,
          },
          children: ['paper1', 'paper'],
          type: 'VerticalSplitPanel',
          parent: 'root',
        },
      },
    ],
  };
};

export default VerticalSplitPanelManager;
